import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  Collapse, 
  List, 
  ListItem, 
  ListItemText, 
  Checkbox, 
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Skeleton,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { search } from '@api/search';
import ProductCard from '../product/ProductCard';
import SearchSuggestions from './SearchSuggestions';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Pagination from '@mui/material/Pagination';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ErrorBoundary from '../error/ErrorBoundary';
import EmbeddedComponentError from '../error/EmbeddedComponentError';
import { logError } from '@utils/errorLogger';

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

// Utility function to normalize facets for Fusion/Cloud and Solr formats
function normalizeFacets(facets) {
  if (!facets) return null;
  let facetFields = null;
  if (facets.fields) {
    facetFields = {};
    Object.entries(facets.fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  } else if (facets.facet_fields) {
    facetFields = {};
    Object.entries(facets.facet_fields).forEach(([field, arr]) => {
      if (Array.isArray(arr)) {
        const buckets = [];
        for (let i = 0; i < arr.length; i += 2) {
          if (arr[i] !== undefined && arr[i+1] !== undefined) {
            buckets.push({ val: arr[i], count: arr[i+1] });
          }
        }
        facetFields[field] = { buckets };
      }
    });
  }
  return facetFields;
}

// Loading skeleton components
function ProductCardSkeleton() {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '4px 4px 0 0' }} />
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={28} width="40%" sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );
}

function FacetSidebarSkeleton() {
  return (
    <Box sx={{ width: '100%', mb: { xs: 3, sm: 0 } }}>
      <Skeleton variant="text" height={32} width="60%" sx={{ mb: 2 }} />
      {[1, 2, 3, 4].map((item) => (
        <Box key={item} sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
          <Box sx={{ pl: 1 }}>
            {[1, 2, 3].map((subItem) => (
              <Box key={subItem} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Skeleton variant="rectangular" width={20} height={20} sx={{ mr: 1 }} />
                <Skeleton variant="text" height={20} sx={{ flex: 1 }} />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function SortSelectorSkeleton({ isMobile = false }) {
  return (
    <Skeleton 
      variant="rectangular" 
      width={isMobile ? 140 : 180} 
      height={isMobile ? 40 : 56}
      sx={{ borderRadius: 1 }}
    />
  );
}

function ViewToggleSkeleton({ isMobile = false }) {
  return (
    <Skeleton 
      variant="rectangular" 
      width={isMobile ? 72 : 88} 
      height={isMobile ? 32 : 40}
      sx={{ borderRadius: 1 }}
    />
  );
}

// Optimized FacetSidebar with debounced search
const FacetSidebar = React.memo(({ facets, selectedFacets, onFacetChange }) => {
  const [openFacets, setOpenFacets] = useState(() => {
    const initialState = {};
    Object.keys(facets || {}).forEach(field => {
      initialState[field] = true;
    });
    return initialState;
  });
  const [facetSearchTerms, setFacetSearchTerms] = useState({});
  const searchTimeouts = useRef({});

  const handleFacetSearch = useCallback((field, searchTerm) => {
    if (searchTimeouts.current[field]) {
      clearTimeout(searchTimeouts.current[field]);
    }

    searchTimeouts.current[field] = setTimeout(() => {
      setFacetSearchTerms(prev => ({
        ...prev,
        [field]: searchTerm
      }));
    }, 300);
  }, []);

  const facetBuckets = useMemo(() => {
    const result = {};
    Object.entries(facets || {}).forEach(([field, facet]) => {
      const searchTerm = facetSearchTerms[field]?.toLowerCase() || '';
      const buckets = facet.buckets || [];
      
      result[field] = {
        filtered: searchTerm 
          ? buckets.filter(bucket => bucket.val.toLowerCase().includes(searchTerm))
          : buckets,
        hasSearchResults: searchTerm ? buckets.some(bucket => bucket.val.toLowerCase().includes(searchTerm)) : true
      };
    });
    return result;
  }, [facets, facetSearchTerms]);

  const toggleFacet = useCallback((field) => {
    setOpenFacets(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(searchTimeouts.current).forEach(clearTimeout);
    };
  }, []);

  if (!facets) return null;

  return (
    <Box sx={{ width: '100%', mb: { xs: 3, sm: 0 } }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
        Filter Results
      </Typography>
      {Object.entries(facets).map(([field, facet]) => {
        const { filtered: filteredBuckets, hasSearchResults } = facetBuckets[field] || { filtered: [], hasSearchResults: false };
        
        return (
          <Box key={field} sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                py: 1,
                borderBottom: '1px solid #eee',
                backgroundColor: 'primary.main',
                px: 1,
                borderRadius: '4px 4px 0 0',
                color: 'white',
              }}
              onClick={() => toggleFacet(field)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, textTransform: 'capitalize', color: 'white' }}>
                {field.replace(/_/g, ' ')}
              </Typography>
              {openFacets[field] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
            <Collapse in={!!openFacets[field]} timeout="auto" unmountOnExit>
              <Box sx={{ p: 1, borderLeft: '1px solid #eee', borderRight: '1px solid #eee', bgcolor: '#fafafa' }}>
                {Array.isArray(facet?.buckets) && facet.buckets.length > 5 && (
                  <TextField
                    size="small"
                    placeholder={`Search ${field.replace(/_/g, ' ')}...`}
                    onChange={(e) => handleFacetSearch(field, e.target.value)}
                    sx={{ 
                      width: '100%', 
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '0.875rem'
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                
                <List dense disablePadding sx={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {Array.isArray(facet?.buckets) && facet.buckets.length > 0 ? (
                    hasSearchResults ? (
                      filteredBuckets.map((bucket) => (
                        <ListItem key={`${field}-${bucket.val}`} disableGutters sx={{ pl: 0 }}>
                          <Checkbox
                            size="small"
                            checked={selectedFacets[field]?.includes(bucket.val) || false}
                            onChange={() => onFacetChange(field, bucket.val)}
                            color="primary"
                          />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <Typography noWrap sx={{ fontSize: 15, maxWidth: '80%' }}>
                                  {bucket.val}
                                </Typography>
                                <Typography sx={{ color: '#888', fontWeight: 400, fontSize: 14 }}>
                                  ({bucket.count})
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem disableGutters sx={{ pl: 0 }}>
                        <ListItemText primary={
                          <Typography sx={{ color: '#888', fontSize: 14, fontStyle: 'italic' }}>
                            No matches found
                          </Typography>
                        } />
                      </ListItem>
                    )
                  ) : (
                    <ListItem disableGutters sx={{ pl: 0 }}>
                      <ListItemText primary={<span style={{ color: '#888', fontSize: 14 }}>No options</span>} />
                    </ListItem>
                  )}
                </List>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
});

// Optimized ViewToggle component with immediate state updates
const ViewToggle = React.memo(({ viewMode, onViewChange, isMobile = false }) => {
  const handleViewChange = useCallback((event, newView) => {
    if (newView && newView !== viewMode) {
      onViewChange(newView);
    }
  }, [viewMode, onViewChange]);

  return (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      onChange={handleViewChange}
      size={isMobile ? 'small' : 'medium'}
      sx={{
        '& .MuiToggleButton-root': {
          border: '1px solid',
          borderColor: 'divider',
          minWidth: isMobile ? 32 : 40,
          padding: isMobile ? '4px' : '8px',
          transition: 'all 0.15s ease-in-out',
          '&.Mui-selected': {
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          },
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }
      }}
    >
      <ToggleButton value='grid' aria-label='grid view'>
        <GridViewIcon fontSize={isMobile ? 'small' : 'medium'} />
      </ToggleButton>
      <ToggleButton value='list' aria-label='list view'>
        <ViewListIcon fontSize={isMobile ? 'small' : 'medium'} />
      </ToggleButton>
    </ToggleButtonGroup>
  );
});

ViewToggle.displayName = 'ViewToggle';

// List view product card component
const ProductListCard = React.memo(({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!product || !product.id) {
    return (
      <Card sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="error">
          Product information unavailable
        </Typography>
      </Card>
    );
  }

  const productTitle = product.title || product.name || 'Unnamed Product';
  const mainImage = product.image_url_s || product.image_url || product.image || 
    `https://placehold.co/120x120/e9e9e9/969696?text=${encodeURIComponent(productTitle)}`;

  // Helper function to format price
  const formatPrice = (price, currency = 'USD', decimalPlaces = 2) => {
    if (!price || isNaN(price)) return null;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      }).format(price);
    } catch (error) {
      return `$${Number(price).toFixed(decimalPlaces)}`;
    }
  };

  return (
    <Card 
      component={Link}
      to={`/product/${product.id}`}
      sx={{ 
        p: 2, 
        mb: 2, 
        display: 'flex', 
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Product Image */}
      <Box sx={{ minWidth: isMobile ? 80 : 120, mr: 2 }}>
        <CardMedia
          component='img'
          height={isMobile ? 80 : 120}
          width={isMobile ? 80 : 120}
          image={mainImage}
          alt={productTitle}
          sx={{
            objectFit: 'contain',
            background: '#f9f9f9',
            borderRadius: 1
          }}
        />
      </Box>

      {/* Product Details */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Typography
          variant={isMobile ? 'subtitle2' : 'h6'}
          sx={{
            fontWeight: 600,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {productTitle}
        </Typography>

        {product.manufacturer && (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 1 }}
          >
            {product.manufacturer}
          </Typography>
        )}

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            mb: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 2 : 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {product.description || 'No description available'}
        </Typography>

        {/* Price and Actions Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            sx={{
              fontWeight: 700,
              color: 'primary.main'
            }}
          >
            {formatPrice(product.price, 'USD', 2) || 'Price unavailable'}
          </Typography>

          <Button
            variant='contained'
            size={isMobile ? 'small' : 'medium'}
            startIcon={<ShoppingCartIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Add to cart logic would go here
            }}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add to Cart
          </Button>
        </Box>
      </Box>
    </Card>
  );
});

ProductListCard.displayName = 'ProductListCard';

// Optimized ProductGrid component
const ProductGrid = React.memo(({ results, loading, resultsPerPage, viewMode = 'grid' }) => {
  if (loading) {
    if (viewMode === 'list') {
      return (
        <Box>
          {Array.from({ length: resultsPerPage }).map((_, index) => (
            <Skeleton
              key={`list-skeleton-${index}`}
              variant='rectangular'
              height={140}
              sx={{ mb: 2, borderRadius: 1 }}
            />
          ))}
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {Array.from({ length: resultsPerPage }).map((_, index) => (
          <Grid item key={`skeleton-${index}`} xs={6} sm={6} md={4} lg={4}>
            <ProductCardSkeleton />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (viewMode === 'list') {
    return (
      <Box>
        {results.map((product, index) => (
          <ErrorBoundary
            key={product.id ? String(product.id) : `product-${index}`}
            componentName={`SearchResultProductList-${product.id || index}`}
            onError={(error, info) => logError(error, { 
              componentName: 'SearchResultProductListCard', 
              productId: product.id,
              title: product.title,
              componentStack: info?.componentStack 
            })}
            fallbackComponent={props => (
              <EmbeddedComponentError 
                {...props} 
                title='Product Display Error' 
                message='This product could not be displayed properly.' 
                minHeight='120px'
                variant='info'
              />
            )}
          >
            <ProductListCard product={product} />
          </ErrorBoundary>
        ))}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {results.map((product, index) => (
        <Grid item key={product.id ? String(product.id) : `product-${index}`} xs={6} sm={6} md={4} lg={4}>
          <ErrorBoundary
            componentName={`SearchResultProduct-${product.id || index}`}
            onError={(error, info) => logError(error, { 
              componentName: 'SearchResultProductCard', 
              productId: product.id,
              title: product.title,
              componentStack: info?.componentStack 
            })}
            fallbackComponent={props => (
              <EmbeddedComponentError 
                {...props} 
                title='Product Display Error' 
                message='This product could not be displayed properly.' 
                minHeight='300px'
                variant='info'
              />
            )}
          >
            <ProductCard product={product} />
          </ErrorBoundary>
        </Grid>
      ))}
    </Grid>
  );
});

// Sort options with proper Lucidworks Fusion syntax
const SORT_OPTIONS = [
  { value: 'score desc', label: 'Relevance (Best Match)', field: 'score', direction: 'desc' },
  { value: 'title asc', label: 'Title (A-Z)', field: 'title', direction: 'asc' },
  { value: 'title desc', label: 'Title (Z-A)', field: 'title', direction: 'desc' },
  { value: 'price asc', label: 'Price (Low to High)', field: 'price', direction: 'asc' },
  { value: 'price desc', label: 'Price (High to Low)', field: 'price', direction: 'desc' }
];

// Memoized sort selector component
const SortSelector = React.memo(({ sortValue, onSortChange, isMobile = false }) => {
  return (
    <FormControl 
      size={isMobile ? 'small' : 'medium'} 
      sx={{ 
        minWidth: isMobile ? 140 : 180,
        '& .MuiSelect-select': {
          fontSize: isMobile ? '0.875rem' : '1rem'
        }
      }}
    >
      <InputLabel id='sort-select-label'>Sort by</InputLabel>
      <Select
        labelId='sort-select-label'
        value={sortValue}
        label='Sort by'
        onChange={(e) => onSortChange(e.target.value)}
        sx={{
          backgroundColor: 'background.paper',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider'
          }
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

SortSelector.displayName = 'SortSelector';

export default function SearchResults() {
  const query = useQuery().get('q') || '';
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const abortControllerRef = useRef(null);
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState(null);
  const [selectedFacets, setSelectedFacets] = useState({});
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortValue, setSortValue] = useState('score desc'); // Default to relevance
  const resultsPerPage = 12;

  // Separate state for immediate UI updates
  const [immediateViewMode, setImmediateViewMode] = useState('grid');

  // Memoized page parsing
  const currentPage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page'), 10);
    return !isNaN(pageParam) && pageParam > 0 ? pageParam : 1;
  }, [location.search]);

  // Memoized sort parsing from URL
  const currentSort = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const sortParam = params.get('sort');
    return sortParam && SORT_OPTIONS.some(opt => opt.value === sortParam) 
      ? sortParam 
      : 'score desc';
  }, [location.search]);

  // Memoized view parsing from URL
  const currentView = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    return viewParam === 'list' ? 'list' : 'grid';
  }, [location.search]);

  // Update both immediate and URL state when URL changes
  useEffect(() => {
    setPage(currentPage);
    setSortValue(currentSort);
    setImmediateViewMode(currentView); // Update immediate state too
  }, [currentPage, currentSort, currentView]);

  // Memoized facets parsing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const facetsParam = params.get('facets');
    
    if (facetsParam) {
      const facetObj = {};
      facetsParam.split('|').forEach(f => {
        const [field, vals] = f.split(':');
        if (field && vals) {
          facetObj[field] = vals.split('~');
        }
      });
      setSelectedFacets(facetObj);
    } else {
      setSelectedFacets({});
    }
  }, [location.search]);

  // Memoized filter queries
  const filterQueries = useMemo(() => {
    return Object.entries(selectedFacets)
      .flatMap(([field, values]) => (values && values.length > 0 ? values.map(val => `${field}:"${val}"`) : []));
  }, [selectedFacets]);

  // Optimized sort change handler
  const handleSortChange = useCallback((newSortValue) => {
    const params = new URLSearchParams(location.search);
    
    if (newSortValue === 'score desc') {
      // Remove sort param for default relevance sort
      params.delete('sort');
    } else {
      params.set('sort', newSortValue);
    }
    
    // Reset to first page when changing sort
    params.set('page', '1');
    
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location, navigate]);

  // Highly optimized view change handler with immediate updates
  const handleViewChange = useCallback((newViewMode) => {
    // Immediate state update for instant UI feedback
    setImmediateViewMode(newViewMode);
    
    // Batch the URL update to avoid blocking the UI
    requestAnimationFrame(() => {
      const params = new URLSearchParams(location.search);
      
      if (newViewMode === 'grid') {
        params.delete('view');
      } else {
        params.set('view', newViewMode);
      }
      
      // Use replace to avoid history pollution
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    });
  }, [location, navigate]);

  // Optimized search effect with sorting
  useEffect(() => {
    if (!query) return;

    const performSearch = async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      setLoading(true);
      setError(null);
      
      try {
        const searchParams = {
          query: query || '*:*',
          start: (page - 1) * resultsPerPage,
          rows: resultsPerPage,
          filterQueries: filterQueries,
          facet: true,
          facetLimit: 50,
          facetMinCount: 1,
          queryProfile: 'default',
          fields: 'id,title,price,price_d,price_f,image_url_s,image_url,description,manufacturer,category,sku,variant_images,variants,total_variants,group',
          sort: sortValue, // Add sort parameter
          collapse: true,
          collapseField: 'group',
          expand: true,
          expandField: 'image_url',
          signal
        };
        
        const result = await search(searchParams);
        
        if (signal.aborted) return;
        
        if (result.success) {
          // Process results to include variant images like FeaturedProducts
          const expanded = result.data?.expanded || {};
          
          const productsWithVariants = (result.documents || []).map(product => {
            const groupId = product.group || product.id;
            const expandedDocs = expanded[groupId]?.docs || [];
            
            // Collect variant data with both image URLs and SKUs from expanded docs
            let variantData = expandedDocs
              .filter(doc => doc.image_url)
              .map(doc => ({
                image_url: doc.image_url,
                sku: doc.sku || doc.id,
                title: doc.title,
                id: doc.id,
                product_id: doc.product_id,
              }));
            
            // Collect all unique image URLs from expanded docs
            let variantImages = expandedDocs
              .map(doc => doc.image_url)
              .filter(Boolean);
            
            // Fallback: If no expanded docs and we have a group, search for other products in the same group
            if (variantImages.length === 0 && product.group && result.documents) {
              const sameGroupProducts = result.documents.filter(p => p.group === groupId && p.id !== product.id);
              variantImages = sameGroupProducts
                .map(p => p.image_url)
                .filter(Boolean);
              
              // Also collect variant data for fallback
              variantData = sameGroupProducts
                .filter(p => p.image_url)
                .map(p => ({
                  image_url: p.image_url,
                  sku: p.sku || p.id,
                  title: p.title,
                  id: p.id,
                  product_id: p.product_id,
                }));
            }
            
            // Always include the main product as first variant if not already included
            const mainVariant = {
              image_url: product.image_url,
              sku: product.sku || product.id,
              title: product.title,
              id: product.id,
              product_id: product.product_id,
            };
            
            if (product.image_url && !variantImages.includes(product.image_url)) {
              variantImages.unshift(product.image_url);
              variantData.unshift(mainVariant);
            }
            
            return {
              ...product,
              variant_images: variantImages,
              variant_data: variantData,
              total_variants: variantImages.length
            };
          });
          
          setResults(productsWithVariants);
          setTotalResults(result.totalResults || 0);
          setFacets(normalizeFacets(result.facets) || null);
        } else {
          setError(result.error || 'No results found.');
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error('[SearchResults] Search error:', error);
          setError(error.message || 'An error occurred while searching');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    // Debounce search calls
    const timeoutId = setTimeout(performSearch, 150);
    
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, page, filterQueries, sortValue]); // Add sortValue to dependencies

  // Optimized facet change handler
  const handleFacetChange = useCallback((field, value) => {
    const current = selectedFacets[field] || [];
    const newSelected = current.includes(value) 
      ? current.filter(v => v !== value)
      : [...current, value];
    
    const updatedFacets = { ...selectedFacets, [field]: newSelected };
    
    // Remove empty arrays
    Object.keys(updatedFacets).forEach(k => {
      if (!updatedFacets[k] || updatedFacets[k].length === 0) {
        delete updatedFacets[k];
      }
    });
    
    const facetsParam = Object.entries(updatedFacets)
      .map(([f, vals]) => `${f}:${vals.join('~')}`)
      .join('|');
    
    const params = new URLSearchParams(location.search);
    if (facetsParam) {
      params.set('facets', facetsParam);
    } else {
      params.delete('facets');
    }
    params.set('page', '1');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [selectedFacets, location, navigate]);

  // Optimized page change handler
  const handlePageChange = useCallback((event, value) => {
    const params = new URLSearchParams(location.search);
    params.set('page', value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location, navigate]);

  // Optimized clear filters handler
  const handleClearFilters = useCallback(() => {
    navigate(`/search?q=${query}`, { replace: true });
    setMobileFiltersOpen(false);
  }, [query, navigate]);

  // Memoized search suggestions container
  const MemoizedSearchSuggestions = React.memo(({ 
    options, 
    visible, 
    onItemClick, 
    onClose,
    maxItems, 
    maxHeight, 
    containerStyles 
  }) => (
    <SearchSuggestions
      options={options}
      visible={visible}
      onItemClick={onItemClick}
      onClose={onClose}
      maxItems={maxItems}
      maxHeight={maxHeight}
      containerStyles={containerStyles}
    />
  ));

  MemoizedSearchSuggestions.displayName = 'MemoizedSearchSuggestions';

  return (
    <Container maxWidth='xl' sx={{ py: 4 }}>
      {/* Header with search query, sort, and view toggle */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: 2,
        gap: 2
      }}>
        <Typography variant='h4' sx={{ 
          fontWeight: 700, 
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          mb: { xs: 0, sm: 0 }
        }}>
          {query === '*:*' ? 'Showing All Products' : `Search Results for "${query}"`}
        </Typography>
        
        {/* Controls container */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          flexWrap: 'wrap'
        }}>
          {!loading && totalResults > 0 && (
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {totalResults.toLocaleString()} result{totalResults !== 1 ? 's' : ''}
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {loading ? (
              <>
                <SortSelectorSkeleton isMobile={isMobile} />
                <ViewToggleSkeleton isMobile={isMobile} />
              </>
            ) : (
              <>
                <SortSelector 
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                  isMobile={isMobile}
                />
                
                <ViewToggle
                  viewMode={immediateViewMode} // Use immediate state for instant feedback
                  onViewChange={handleViewChange}
                  isMobile={isMobile}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      
      {/* Mobile filter button */}
      {isMobile && (facets || loading) && (
        <Box sx={{ mb: 2 }}>
          <Button 
            variant='outlined' 
            startIcon={<FilterListIcon />}
            onClick={() => setMobileFiltersOpen(true)}
            fullWidth
            sx={{ borderRadius: 2 }}
            disabled={loading}
          >
            Filter Results
          </Button>
        </Box>
      )}
      
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}
      {!loading && !error && results.length === 0 && (
        <Typography>No results found.</Typography>
      )}
      
      <Grid container spacing={3} alignItems="flex-start">
        {/* Desktop facet sidebar */}
        {((facets && !isMobile) || (loading && !isMobile)) && (
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper elevation={0} sx={{ 
              p: 3, 
              borderRadius: 2, 
              bgcolor: '#f8fafc', 
              border: '1px solid #eee', 
              position: 'sticky', 
              top: 24,
              width: '100%'
            }}>
              {loading ? (
                <FacetSidebarSkeleton />
              ) : (
                <FacetSidebar facets={facets} selectedFacets={selectedFacets} onFacetChange={handleFacetChange} />
              )}
            </Paper>
          </Grid>
        )}
        
        <Grid item xs={12} md={(facets && !isMobile) || (loading && !isMobile) ? 9 : 12} lg={(facets && !isMobile) || (loading && !isMobile) ? 9.5 : 12}>
          <Box sx={{ width: '100%' }}>
            <ProductGrid 
              results={results} 
              loading={loading} 
              resultsPerPage={resultsPerPage}
              viewMode={immediateViewMode} // Use immediate state here too
            />
            
            {/* Pagination */}
            {!loading && totalResults > resultsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, overflow: 'auto', py: 1 }}>
                <Pagination
                  count={Math.ceil(totalResults / resultsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color='primary'
                  shape='rounded'
                  size={isMobile ? 'small' : 'medium'}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      
      {/* Mobile filters drawer */}
      <Drawer
        anchor="bottom"
        open={isMobile && mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ 
          sx: { 
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh'
          } 
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          borderBottom: '1px solid #eee' 
        }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>Filter Results</Typography>
          <IconButton onClick={() => setMobileFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Sort and view controls in mobile drawer */}
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {loading ? (
              <>
                <SortSelectorSkeleton isMobile={true} />
                <ViewToggleSkeleton isMobile={true} />
              </>
            ) : (
              <>
                <SortSelector 
                  sortValue={sortValue}
                  onSortChange={handleSortChange}
                  isMobile={true}
                />
                
                <ViewToggle
                  viewMode={immediateViewMode} // Use immediate state in drawer too
                  onViewChange={handleViewChange}
                  isMobile={true}
                />
              </>
            )}
          </Box>
        </Box>
        
        <Box sx={{ p: 2, maxHeight: 'calc(90vh - 120px)', overflow: 'auto' }}>
          {loading ? (
            <FacetSidebarSkeleton />
          ) : facets ? (
            <FacetSidebar facets={facets} selectedFacets={selectedFacets} onFacetChange={handleFacetChange} />
          ) : null}
        </Box>
        <Box sx={{ p: 2, borderTop: '1px solid #eee', display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            sx={{ flex: 1 }}
            disabled={loading}
          >
            Clear All
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setMobileFiltersOpen(false)}
            sx={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Drawer>
    </Container>
  );
}
